import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { Calendar, Car, CheckCircle, FileStack, Grid3x3, Package, Users } from "lucide-react";
import { useEffect } from "react";

import { useProfile } from "@/hooks/use-profile";
import type { UserRole } from "@/lib/roles";
import { isAdmin, isEditor } from "@/lib/roles";

export const Route = createFileRoute("/_auth/app/admin")({
  component: AdminLayout,
});

const NAV_ITEMS = [
  { to: "/app/admin/approvals", label: "Approvals", icon: CheckCircle },
  { to: "/app/admin/setups", label: "Setups", icon: FileStack },
  { to: "/app/admin/packages", label: "Packages", icon: Package },
  { to: "/app/admin/users", label: "Users", icon: Users },
  { to: "/app/admin/schedule", label: "Schedule", icon: Calendar },
  { to: "/app/admin/assignments", label: "Assignments", icon: Grid3x3 },
  { to: "/app/admin/cars", label: "Car Images", icon: Car },
] as const;

function AdminLayout() {
  const { data: profile, isLoading } = useProfile();
  const navigate = useNavigate();

  const profileReady = !isLoading && profile !== undefined;
  const role = (profile?.role ?? "user") as UserRole;
  const hasAccess = isAdmin(role) || isEditor(role);

  useEffect(() => {
    if (profileReady && !hasAccess) {
      navigate({ to: "/app/setups", search: {} as any });
    }
  }, [profileReady, hasAccess, navigate]);

  if (!profileReady) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  return (
    <div className="flex gap-6">
      {/* Sidebar */}
      <aside className="hidden w-56 shrink-0 md:block">
        <nav className="sticky top-24 space-y-1 rounded-xl border border-border/50 bg-card p-3">
          <h2 className="mb-3 px-3 font-[Bebas_Neue] text-lg text-foreground">Admin Panel</h2>
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground [&.active]:bg-accent [&.active]:text-foreground"
              activeProps={{ className: "active" }}
            >
              <Icon className="h-4 w-4" />
              {label}
            </Link>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {/* Mobile nav */}
        <div className="scrollbar-visible mb-4 flex gap-2 overflow-x-auto md:hidden">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-border/50 bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground [&.active]:border-primary/50 [&.active]:text-foreground"
              activeProps={{ className: "active" }}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </Link>
          ))}
        </div>
        <Outlet />
      </div>
    </div>
  );
}
